import React, { useEffect, useRef, useState } from 'react'
import GraphQLErrorList from '../components/graphql-error-list'
import SEO from '../components/seo'
import Layout from '../containers/layout'
import Container from '../components/container'
import CloudIndexNav from '../components/cloudIndexNav/cloudIndexNav'
import { useWindowSize } from '../hooks/useWindowSize.hook'
import * as styles from './radar.module.css'
import linkStyle from '../styles/components/links.module.css'
import { graphql, Link } from 'gatsby'
import Icon from '../components/icons'
import InfraredReportHero from '../components/infraredReportHero/infraredReportHero'
import Lottie from '../components/Lottie'

export const query = graphql`
    query RadarReports {
        page: sanityPage(title: { eq: "Radar" }) {
            title
            _rawBody
            metaTitle
            metaDescription
            metaKeywords
            metaImage {
                asset {
                    _id
                    url
                }
            }
            radar {
                hero {
                    backgroundVideo {
                        asset {
                            _id
                            url
                        }
                    }
                    videoPoster {
                        asset {
                            _id
                            url
                        }
                    }
                    headingImage {
                        asset {
                            _id
                            url
                        }
                    }
                    heading
                    text
                }
                secondSection {
                    heading
                    text
                    downloadButtonText
                    downloadButtonTextMobile
                }
                thirdSection {
                    subheading
                    heading
                    text
                    lottieFile {
                        asset {
                            _id
                            url
                        }
                    }
                    lottieFileTablet {
                        asset {
                            _id
                            url
                        }
                    }
                    lottieFileMobile {
                        asset {
                            _id
                            url
                        }
                    }
                }
                fourthSection {
                    subheading
                    heading
                    text
                    lottieFile {
                        asset {
                            _id
                            url
                        }
                    }
                }
                fifthSection {
                    subheading
                    heading
                    text
                    lottieFile {
                        asset {
                            _id
                            url
                        }
                    }
                    lottieHeading
                }
                sixthSection {
                    backgroundImage {
                        asset {
                            _id
                            url
                        }
                    }
                    heading
                    text
                    downloadButtonText
                    downloadButtonTextMobile
                    documents {
                        year
                        googleDriveLink
                    }
                }
            }
        }
    }
`

const CloudReportPage = props => {
  const {
    data,
    errors
  } = props

  const filterRef = useRef()
  const [selectedReport, setSelectedReport] = useState(2025)
  const [selectedReportUrl, setSelectedReportUrl] = useState()
  const { width } = useWindowSize()

  const reports = data.page?.radar?.sixthSection?.documents.map(report => ({
    year: report.year,
    url: report.googleDriveLink
  })) || []

  const getAnimationData = () => {
    const { lottieFile, lottieFileTablet, lottieFileMobile } = data.page?.radar?.thirdSection || {}

    if (width > 1024 && lottieFile) return lottieFile
    if (width > 768 && lottieFileTablet) return lottieFileTablet
    if (lottieFileMobile) return lottieFileMobile

    return lottieFile
  }

  const filterSelect = (e) => {
    const selection = e.target.value
    setSelectedReport(selection)
  }

  function previewLink (viewLink) {
    if (!viewLink) return null
    if (viewLink.includes('/view')) {
      return viewLink.replace('/view', '/preview')
    } else if (viewLink.includes('/preview')) {
      return viewLink + '/preview'
    }
  }

  function downloadLink (viewLink) {
    if (!viewLink) return null
    const fileIdMatch = viewLink.match(/\/d\/([a-zA-Z0-9_-]+)\//)
    if (fileIdMatch && fileIdMatch[1]) {
      const fileId = fileIdMatch[1]
      return `https://drive.google.com/uc?export=download&id=${fileId}`
    } else {
      return viewLink
    }
  }

  function trackDownload (report) {
    if (!report) return
    if (typeof gtag !== 'undefined') {
      gtag('event', `download_the_report_${report}`, {
        component: 'button'
      })
    }
  }

  function getFileUrlByYear () {
    const report = reports.find(doc => doc.year === selectedReport)
    if (report) {
      setSelectedReportUrl(report.url ? previewLink(report.url) : null)
    }
  }

  useEffect(() => {
    if (filterRef.current) {
      const selection = filterRef.current.value
      setSelectedReport(selection)
    }
  }, [reports])

  useEffect(() => {
    getFileUrlByYear()
  }, [selectedReport])

  if (errors) {
    return (
      <Layout>
        <GraphQLErrorList errors={errors} />
      </Layout>
    )
  }

  return (
    <Layout page='cloudReport' palette='deepBlack' classes={`${styles.pattern} ${styles.whiteColor}`}>
      <SEO
        title={data?.page?.metaTitle || data?.page?.title} description={data?.page?.metaDescription}
        keywords={data?.page?.metaKeywords ? data?.page?.metaKeywords.split(',') : []}
        imageFB={data?.page?.metaImage.asset.url}
      />
      <InfraredReportHero video={data?.page?.radar?.hero?.backgroundVideo} videoPoster={data?.page?.radar?.hero?.videoPoster} addClass='reportHero'>
        <div>
          <h1 style={{ display: 'none' }}>Redpoint Radar: The State of Talent in 2025</h1>
          {data?.page?.radar?.hero?.heading && <h2>{data?.page?.radar?.hero?.heading}</h2>}
          <img className={styles.reportHeroHeader} src={data?.page?.radar?.hero?.headingImage.asset.url} alt='The InfraRed Report' />
          {data?.page?.radar?.hero?.text && <h2>{data?.page?.radar?.hero?.text}</h2>}
        </div>
        <div>
          <section className={`${styles.secondSection}`}>
            <div className={styles.secondSectionWrapper}>
              <div className={styles.secondSectionText}>
                {data?.page?.radar?.secondSection?.heading && (<h2>{data.page.radar.secondSection.heading}</h2>)}
                {data?.page?.radar?.secondSection?.text && (<p>{data.page.radar.secondSection.text}</p>)}
              </div>
              <div className={styles.secondSectionButton}>
                <div style={{
                  display: 'flex',
                  marginTop: 10
                }}
                >
                  <a
                    href={downloadLink(selectedReportUrl)}
                    onClick={() => trackDownload(selectedReport)}
                    style={{
                      fontSize: 24
                    }}
                    rel='noopener noreferrer'
                    target='_blank'
                    className={`${linkStyle.linkArrow} ${linkStyle.white} ${linkStyle.noAnim} ${styles.downloadReportBtn}`}
                  >{width > 768 ? data?.page?.radar?.secondSection?.downloadButtonText : data?.page?.radar?.secondSection?.downloadButtonTextMobile}
                    <span>
                      <div className={`${linkStyle.icon}`}>
                        <Icon width={32} height={32} symbol='downArrowIcon' color='currentColor' />
                      </div>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </InfraredReportHero>

      <Container class={styles.container}>

        <section className={`${styles.thirdSection} ${styles.separator}`}>
          <div className={`${styles.primarySectionWrapper} ${styles.orderReversed} ${styles.alignCenter}`}>
            <div className={styles.primarySectionText}>
              {data?.page?.radar?.thirdSection?.subheading && (<div className={styles.primarySectionSubtitle}>{data.page.radar.thirdSection.subheading}</div>)}
              {data?.page?.radar?.thirdSection?.heading && (<h2>{data.page.radar.thirdSection.heading}</h2>)}
              {data?.page?.radar?.thirdSection?.text && (<p>{data.page.radar.thirdSection.text}</p>)}
            </div>
            <div className={styles.thirdSectionLottie} style={{ maxWidth: '530px' }}>
              {getAnimationData() && (<LottiePlayer lottieFile={getAnimationData()} />)}
            </div>
          </div>
        </section>

        <section className={`${styles.fourthSection} ${styles.separator} ${styles.mAlignCenter}`}>
          <div className={styles.primarySectionWrapper}>
            <div className={`${styles.primarySectionText}`}>
              {data?.page?.radar?.fourthSection?.subheading && (<div className={styles.primarySectionSubtitle}>{data.page.radar.fourthSection.subheading}</div>)}
              {data?.page?.radar?.fourthSection?.heading && (<h2>{data.page.radar.fourthSection.heading}</h2>)}
              {data?.page?.radar?.fourthSection?.text && (<p>{data.page.radar.fourthSection.text}</p>)}
            </div>
            <div className={styles.fourthSectionLottie} style={{ maxWidth: '477px' }}>
              {data?.page?.radar?.fourthSection?.lottieFile && (<LottiePlayer lottieFile={data?.page?.radar?.fourthSection?.lottieFile} />)}
            </div>
          </div>
        </section>

        <section className={`${styles.fifthSection} ${styles.separator}`}>
          <div className={styles.fourthSectionWrapper}>
            <div className={`${styles.primarySectionText} ${styles.center}`}>
              {data?.page?.radar?.fifthSection?.subheading && (<div className={styles.primarySectionSubtitle}>{data.page.radar.fifthSection.subheading}</div>)}
              {data?.page?.radar?.fifthSection?.heading && (<h2>{data.page.radar.fifthSection.heading}</h2>)}
              {data?.page?.radar?.fifthSection?.text && (<p>{data.page.radar.fifthSection.text}</p>)}
            </div>
            <div className={styles.fifthSectionLottie}>
              {data?.page?.radar?.fifthSection?.lottieHeading && (<h3>{data.page.radar.fifthSection.lottieHeading}</h3>)}
              {data?.page?.radar?.fifthSection?.lottieFile && (<LottiePlayer lottieFile={data?.page?.radar?.fifthSection?.lottieFile} />)}
            </div>
          </div>
        </section>

      </Container>
      {reports.length > 0 && (
        <section className={`${styles.sixthSection}`} style={{ paddingTop: 0 }}>
          <div className={styles.sixthSectionWrapper} style={{ backgroundImage: data?.page?.radar?.sixthSection?.backgroundImage?.asset?.url ? `url(${data?.page?.radar?.sixthSection?.backgroundImage?.asset?.url})` : '' }}>
            <div className={styles.secondSectionButton}>
              <div className={styles.reportFilter}>
                <div className={styles.filter}>
                  <div className={styles.label}>Filter</div>
                  <div className={styles.dropdown}>
                    <select ref={filterRef} onChange={filterSelect}>
                      {reports.length > 0 && reports.map((report, index) => (
                        <option key={index} value={report.year}>{report.year} Report</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className={`${styles.secondSectionText} ${styles.fixWidth}`}>
              {data?.page?.radar?.sixthSection?.heading && (<h2>{data.page.radar.sixthSection.heading}</h2>)}
              {data?.page?.radar?.sixthSection?.text && (<p>{data.page.radar.sixthSection.text}</p>)}
              <div style={{
                display: 'flex',
                marginTop: 10
              }}
              >
                <a
                  href={downloadLink(selectedReportUrl)}
                  onClick={() => trackDownload(selectedReport)}
                  style={{
                    fontSize: 24
                  }}
                  rel='noopener noreferrer'
                  target='_blank'
                  className={`${linkStyle.linkArrow} ${linkStyle.white} ${linkStyle.noAnim} ${styles.downloadReportBtn}`}
                >{width > 768 ? data?.page?.radar?.sixthSection?.downloadButtonText : data?.page?.radar?.sixthSection?.downloadButtonTextMobile}
                  <span>
                    <div className={`${linkStyle.icon}`}>
                      <Icon width={32} height={32} symbol='downArrowIcon' color='currentColor' />
                    </div>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </section>
      )}
    </Layout>
  )
}

const LottiePlayer = ({ lottieFile }) => {
  const [animationData, setAnimationData] = useState(null)
  const containerRef = useRef(null)
  const lottieRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (lottieFile?.asset?.url) {
      fetch(lottieFile.asset.url)
        .then(response => response.json())
        .then(data => setAnimationData(data))
        .catch(error => console.error('Error loading Lottie file:', error))
    }
  }, [lottieFile])

  useEffect(() => {
    if (!animationData || !containerRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      {
        threshold: 0.5
      }
    )

    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [animationData])

  useEffect(() => {
    if (isVisible && lottieRef.current) {
      lottieRef.current.play()
    }
  }, [isVisible])

  if (!animationData) return <></>

  return (
    <div ref={containerRef}>
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop
        autoplay={false}
      />
    </div>
  )
}

export default CloudReportPage
